import React, { useCallback, useMemo } from 'react';
import { Pressable, View } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import { Feather } from '@expo/vector-icons';
import ServiceNoteButton from '../driver/components/ServiceNoteButton';
import LocationDetailView from '../location/LocationDetailView';
import MachineDetailView from './MachineDetailView';
import PointOfSalePlanogramView from './PointOfSalePlanogramView';
import { useUserContext } from '../../../common/utils';
import { ROLE_PERMISSIONS } from '../../../common/constants';
import { Text } from '../../components';
import { Screen } from '../../design/components';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const Tab = createMaterialTopTabNavigator();

const MachineDetailTabView = () => {
	const navigation = useNavigation();
	const { hasPermission } = useUserContext();
	const { theme: { ui: { colors, type } } } = useTheme();
	const styles = useThemedStyles(createStyles);

	const route = useRoute();
	const {
		machineId,
		machineName,
		locationId,
		isMarket,
		locationTab,
		docketId,
	} = route.params;

	const posName = isMarket ? 'Market' : 'Machine';
	const initialRouteName = locationTab ? 'Location' : posName; // default tab to show by name
	const canAddTickets = Boolean(hasPermission?.(ROLE_PERMISSIONS.ADD_EDIT_TICKETS));

	const openTicketCreator = useCallback(() => {
		navigation.push('TicketCreator', {
			machineId,
			docketId,
		});
	}, [docketId, machineId, navigation]);

	const tabScreenOptions = useMemo(() => ({
		tabBarIndicatorStyle: {
			backgroundColor: colors.accent,
			height: 3,
		},
		tabBarStyle: {
			backgroundColor: colors.surface,
			borderBottomWidth: 1,
			borderBottomColor: colors.border,
			elevation: 0,
			shadowOpacity: 0,
		},
		tabBarActiveTintColor: colors.accent,
		tabBarInactiveTintColor: colors.textSecondary,
		tabBarLabelStyle: {
			...type.bodySmallSemibold,
			textTransform: 'none',
		},
		tabBarItemStyle: {
			minHeight: 48,
		},
		sceneStyle: {
			backgroundColor: colors.canvas,
		},
	}), [colors, type]);

	return (
		<Screen
			title={machineName || posName}
			scroll={false}
			headerRight={(
				<View style={styles.headerActions}>
					<ServiceNoteButton />
					{canAddTickets && (
						<Pressable
							onPress={openTicketCreator}
							accessibilityRole="button"
							accessibilityLabel="Create ticket"
							style={({ pressed }) => [styles.headerAction, pressed && styles.pressed]}
						>
							<Feather name="plus" size={18} color={colors.accent} />
							<Text style={styles.headerActionLabel}>Ticket</Text>
						</Pressable>
					)}
				</View>
			)}
		>
			<Tab.Navigator
				screenOptions={tabScreenOptions}
				initialRouteName={initialRouteName}
			>
				<Tab.Screen name={isMarket ? 'Market' : 'Machine'}>
					{props => <MachineDetailView {...props} machineId={machineId} machineName={machineName} />}
				</Tab.Screen>
				<Tab.Screen name="Location">
					{props => <LocationDetailView {...props} locationId={locationId} hideDirections={true} />}
				</Tab.Screen>
				<Tab.Screen name="Planogram">
					{props => (
						<PointOfSalePlanogramView
							{...props}
							pointOfSaleId={machineId}
							pointOfSaleName={machineName}
						/>
					)}
				</Tab.Screen>
			</Tab.Navigator>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space, touch } }) => ({
	headerActions: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.xs,
	},
	headerAction: {
		minHeight: touch.min,
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.xs,
		paddingHorizontal: space.sm,
	},
	headerActionLabel: {
		...type.bodySmallStrong,
		color: colors.accent,
	},
	pressed: {
		opacity: 0.7,
	},
});

export default MachineDetailTabView;
